import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  modifyModalRecoil,
  userModifyModalRecoil,
} from '@/atom/ModalAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import ModalType1 from '@/modal/ModalType1';
import { hangjungdong } from '@/data/hangjungdong';
import { UserApi } from '@/sdk/UserApi';
import { UpdateUserDtoTx } from '@/sdk/data-contracts';
import {
  userListRecoil,
  userTypesRecoil,
  userSitesRecoil,
} from '@/atom/UserAtom';

interface UserModifyModalProps {
  id: number;
}

const UserModifyModal = ({ id }: UserModifyModalProps) => {
  const userApi = new UserApi({ baseURL: Config.API_URL });
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const setUserModifyModalState = useSetRecoilState(userModifyModalRecoil);
  const [userTypesState, setUserTypesState] = useRecoilState(userTypesRecoil);
  const [userSitesState, setUserSitesState] = useRecoilState(userSitesRecoil);

  const [userType, setUserType] = useState<string>('');
  const [site, setSite] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [passid, setPassid] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [sido, setSido] = useState<string>('');
  const [sigungu, setSigungu] = useState<string>('');
  const [eupmyeondong, setEupmyeondong] = useState<string>('');
  const [department, setDepartment] = useState<string>('');
  const [departmentPhone, setDepartmentPhone] = useState<string>('');
  const [note, setNote] = useState<string>('');

  const fetchUserModify = async () => {
    try {
      const { data } = await userApi.userControllerGetUser(Number(id), {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });

      const transformSido =
        hangjungdong.sido.find((list) => list.codeNm === data.sido)?.sido ||
        '전국';
      const transformSigungu =
        hangjungdong.sigungu.find((list) => list.codeNm === data.sigungu)
          ?.sigungu || '';
      const transformEupmyeondong =
        hangjungdong.dong.find((list) => list.codeNm === data.eupmyeondong)
          ?.dong || '';

      setUserType(data.userType);
      setSite(data.site);
      setName(data.name);
      setPhone(data.phone);
      setPassid(data.passid);
      setEmail(data.email);
      setSido(String(transformSido));
      setSigungu(String(transformSigungu));
      setEupmyeondong(String(transformEupmyeondong));
      setDepartment(data.department);
      setDepartmentPhone(data.departmentPhone);
      setNote(data.note);
    } catch (err: any) {}
  };

  const handleSubmit = async () => {
    if (
      userType === '' ||
      site === '' ||
      name.trim() === '' ||
      phone.trim() === '' ||
      email.trim() === ''
    ) {
      setErrorModalText('빈 칸을 확인해 주세요.');
      return;
    }

    const phoneRegex =
      /^(010\d{8}|02\d{8}|070\d{8}|0507\d{8}|1[5-9]\d{6}|043\d{7})$/;

    const phoneTypeCheck = phoneRegex.test(phone);
    let affiliationPhoneTypeCheck = true;
    if (departmentPhone.trim() !== '') {
      affiliationPhoneTypeCheck = phoneRegex.test(departmentPhone);
    }

    if (sido !== '전국' && (sigungu === '' || eupmyeondong === '')) {
      setErrorModalText('빈 칸을 확인해 주세요.');
      return;
    }

    if (!phoneTypeCheck) {
      setErrorModalText('휴대폰번호 형식을 확인해 주세요.');
      return;
    }

    if (!affiliationPhoneTypeCheck) {
      setErrorModalText('소속 연락처 형식을 확인해 주세요.');
      return;
    }

    const transformSido =
      hangjungdong.sido.find((list) => list.sido === sido)?.codeNm || '전국';
    const transformSigungu = hangjungdong.sigungu.find(
      (list) => list.sido === sido && list.sigungu === sigungu,
    )?.codeNm;
    const transformEupmyeondong = hangjungdong.dong.find(
      (list) =>
        list.sido === sido &&
        list.sigungu === sigungu &&
        list.dong === eupmyeondong,
    )?.codeNm;

    const dto: UpdateUserDtoTx = {
      userType,
      site,
      name,
      phone,
      email,
      sido: transformSido,
      sigungu: transformSido === '전국' ? '' : transformSigungu,
      eupmyeondong: transformSido === '전국' ? '' : transformEupmyeondong,
      department,
      departmentPhone,
      note,
    };

    try {
      await userApi.userControllerUpdateUser(id, dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setCompleteModalState(true);
    } catch (err: any) {}
  };

  useEffect(() => {
    fetchUserModify();
  }, []);

  return (
    <>
      <ModalType1 width={1000}>
        <div className="title-box flex aic jcsb">
          <h1>회원 수정</h1>
          <button
            className="x-btn img-box"
            onClick={() => setUserModifyModalState(false)}
          >
            <img src="/img/ico_x_444.svg" alt="" />
          </button>
        </div>
        <div className="cnt-area flex fdc">
          <div className="cnt-wrap flex fdc">
            <div className="cnt-box flex fdc">
              <div className="cnt-title-box flex jcsb aie">
                <div className="left-box">
                  <h2 className="tm">기본 정보</h2>
                </div>
                <div className="right-box flex">
                  <h3>
                    <span className="inblock">*</span>필수 항목
                  </h3>
                </div>
              </div>
              <CS.TableType2>
                <table>
                  <tbody>
                    <tr>
                      <th>
                        회원구분<span className="star-point">*</span>
                      </th>
                      <td>
                        <IS.SelectType1 style={{ width: 190 }}>
                          <select
                            value={userType}
                            onChange={(e) => setUserType(e.target.value)}
                            className="selected"
                          >
                            <option value="">선택</option>
                            {userTypesState.map((type, index) => (
                              <option key={index} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                          <span className="img-box">
                            <img src="/img/arrow_bottom_555.svg" alt="" />
                          </span>
                        </IS.SelectType1>
                      </td>
                      <th>
                        사이트<span className="star-point">*</span>
                      </th>
                      <td>
                        <IS.SelectType1 style={{ width: 190 }}>
                          <select
                            value={site}
                            onChange={(e) => setSite(e.target.value)}
                            className="selected"
                          >
                            <option value="">선택</option>
                            {userSitesState.map((type, index) => (
                              <option key={index} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                          <span className="img-box">
                            <img src="/img/arrow_bottom_555.svg" alt="" />
                          </span>
                        </IS.SelectType1>
                      </td>
                    </tr>
                    <tr>
                      <th>
                        이름<span className="star-point">*</span>
                      </th>
                      <td>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </td>
                      <th>
                        휴대폰번호<span className="star-point">*</span>
                      </th>
                      <td>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="text"
                          value={phone}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*$/.test(value)) {
                              setPhone(value);
                            }
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>
                        아이디<span className="star-point">*</span>
                      </th>
                      <td colSpan={3}>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="text"
                          value={passid}
                          disabled={true}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>
                        이메일<span className="star-point">*</span>
                      </th>
                      <td colSpan={3}>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="text"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CS.TableType2>
            </div>
            <div className="cnt-box flex fdc">
              <div className="cnt-title-box flex jcsb aie">
                <div className="left-box">
                  <h2 className="tm">기타 정보</h2>
                </div>
              </div>
              <CS.TableType2>
                <table>
                  <tbody>
                    <tr>
                      <th>
                        지역<span className="star-point">*</span>
                      </th>
                      <td>
                        <div className="td-box">
                          <IS.SelectType1 style={{ width: 190 }}>
                            <select
                              value={sido}
                              onChange={(e) => {
                                setSido(e.target.value);
                                setSigungu('');
                              }}
                              className="selected"
                            >
                              <option value="전국">전국</option>
                              {hangjungdong.sido.map((list) => (
                                <option key={list.sido} value={list.sido}>
                                  {list.codeNm}
                                </option>
                              ))}
                            </select>
                            <span className="img-box">
                              <img src="/img/arrow_bottom_555.svg" alt="" />
                            </span>
                          </IS.SelectType1>
                          <IS.SelectType1 style={{ width: 190 }}>
                            <select
                              value={sigungu}
                              onChange={(e) => {
                                setSigungu(e.target.value);
                                setEupmyeondong('');
                              }}
                              className="selected"
                              disabled={sido === '전국'}
                            >
                              <option value="">-시/군/구-</option>
                              {sido &&
                                hangjungdong.sigungu
                                  .filter((list) => list.sido === sido)
                                  .map((list) => (
                                    <option
                                      key={list.sigungu}
                                      value={list.sigungu}
                                    >
                                      {list.codeNm}
                                    </option>
                                  ))}
                            </select>
                            <span className="img-box">
                              <img src="/img/arrow_bottom_555.svg" alt="" />
                            </span>
                          </IS.SelectType1>
                          <IS.SelectType1 style={{ width: 190 }}>
                            <select
                              value={eupmyeondong}
                              onChange={(e) => setEupmyeondong(e.target.value)}
                              className="selected"
                              disabled={sido === '전국'}
                            >
                              <option value="">-동/읍/면-</option>
                              {sigungu &&
                                hangjungdong.dong
                                  .filter(
                                    (list) =>
                                      list.sido === sido &&
                                      list.sigungu === sigungu,
                                  )
                                  .map((list) => (
                                    <option key={list.dong} value={list.dong}>
                                      {list.codeNm}
                                    </option>
                                  ))}
                            </select>
                            <span className="img-box">
                              <img src="/img/arrow_bottom_555.svg" alt="" />
                            </span>
                          </IS.SelectType1>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>소속</th>
                      <td>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="text"
                          value={department}
                          onChange={(e) => {
                            setDepartment(e.target.value);
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>소속 연락처</th>
                      <td>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="text"
                          value={departmentPhone}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*$/.test(value)) {
                              setDepartmentPhone(value);
                            }
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>비고</th>
                      <td>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="text"
                          value={note}
                          onChange={(e) => {
                            setNote(e.target.value);
                          }}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CS.TableType2>
            </div>
          </div>
          <div className="btn-wrap">
            <BS.Btn2Type3
              type="button"
              onClick={() => setUserModifyModalState(false)}
            >
              취소
            </BS.Btn2Type3>
            <BS.Btn2Type4 type="button" onClick={handleSubmit}>
              수정
            </BS.Btn2Type4>
          </div>
        </div>
      </ModalType1>
    </>
  );
};

export default UserModifyModal;
